import { Children, isValidElement } from 'react';
import { Link } from 'react-router-dom';

// Swap this one value to change the contact address everywhere on these pages.
export const CONTACT_EMAIL = 'cumarfarah247@gmail.com';

// Set to true to mark the privacy policy and terms as drafts again.
export const SHOW_DRAFT_BANNER = false;

export const REGISTERED_OFFICE = '82a James Carter Road, Mildenhall, Bury St Edmunds, IP28 7DE';
const OPERATOR_NAME = 'Aston Metrics Ltd';
export const LEGAL_LAST_UPDATED = '4 October 2026';

function Email() {
  return <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>;
}

/** One part of a policy: a heading and its reading text. */
function Section({ id, title, children }) {
  return <section className="mk-legal-section" id={id} aria-labelledby={`${id}-heading`}>
    <h2 className="mk-legal-heading" id={`${id}-heading`}>{title}</h2>
    {children}
  </section>;
}

/** A short bold lead-in followed by its explanation. */
function Point({ lead, children }) {
  return <div className="mk-legal-point">
    <p className="mk-legal-point-lead">{lead}</p>
    {children}
  </div>;
}

function sectionsIn(children) {
  return Children.toArray(children).flatMap((child) => {
    if (!isValidElement(child)) return [];
    if (child.type === Section) return [child];
    return sectionsIn(child.props.children);
  });
}

/** Title band, then an "On this page" list beside the reading column. */
function LegalShell({ title, children, draft = false }) {
  const toc = sectionsIn(children);
  return <div className="mk-legal">
    <header className="mk-container mk-legal-title">
      <h1 className="mk-display">{title}</h1>
      <p className="mk-legal-updated">Last updated: {LEGAL_LAST_UPDATED}</p>
    </header>
    {draft && SHOW_DRAFT_BANNER && <p className="mk-container mk-legal-draft" role="note">Draft for review. Not yet legal advice.</p>}
    <div className="mk-container mk-legal-body">
      <nav className="mk-legal-toc" aria-label="On this page">
        <p className="mk-legal-toc-title">On this page</p>
        <ul>{toc.map((item) => <li key={item.props.id}><a href={`#${item.props.id}`}>{item.props.title}</a></li>)}</ul>
      </nav>
      <div className="mk-legal-content">{children}</div>
    </div>
  </div>;
}

export function PrivacyPage() {
  return <LegalShell title="Privacy Policy" draft>
    <Section id="who-we-are" title="Who We Are">
      <p>Resident Service 360 is run by {OPERATOR_NAME}, a company registered in England and Wales (company number 17114120), with its registered office at {REGISTERED_OFFICE}.</p>
      <p>We are the “controller” of your personal data. This means we decide how it is used and we are responsible for looking after it.</p>
      <p>If you have any questions, email us at <Email/>.</p>
    </Section>

    <Section id="what-we-collect" title="What We Collect">
      <p>We only collect what we need to run Resident Service 360. What we hold depends on how you use it.</p>
      <h3>When you create an account</h3>
      <ul>
        <li>Your name.</li>
        <li>Your email address.</li>
        <li>Your mobile number.</li>
        <li>Your password. Our database provider, Supabase, stores it in a scrambled (hashed) form. We cannot read it.</li>
        <li>Your account type, for example resident, homeowner, supplier or business.</li>
      </ul>
      <h3>If you are a resident or homeowner</h3>
      <ul>
        <li>The addresses and postcodes of your homes or premises.</li>
        <li>Requests to link a property to your housing provider, and any message you add.</li>
        <li>Repair reports: the title, description, how urgent it is, its status and any appointment times.</li>
        <li>Updates and messages about a repair.</li>
        <li>Feedback you send about repairs or the service.</li>
        <li>Jobs you post: the title, description, type of job, general area, deadline and the exact service address.</li>
        <li>Photos you add to repair reports and jobs.</li>
        <li>Who handles repairs at your home, if you add them: your landlord's or letting agent's name, email and phone number, and whether you let us contact them.</li>
        <li>Repair reports you send to your landlord or agent, and the date you told us you sent them.</li>
      </ul>
      <h3>If you are a supplier or business</h3>
      <ul>
        <li>Your company name.</li>
        <li>Your company registration number and the country it is registered in.</li>
        <li>Who in your company has access, and their role.</li>
        <li>Bids and responses you send: your message and your price.</li>
        <li>Jobs and tenders you publish as a buyer.</li>
        <li>Your trades, the areas you cover and any certificates or registration numbers you add, such as Gas Safe.</li>
        <li>Your approximate location (to about 1 km), only if you allow it, to show jobs near you.</li>
      </ul>
      <h3>Payments and notifications</h3>
      <ul>
        <li>If you pay a supplier in the app, Stripe handles your card details. We never see your full card number.</li>
        <li>If you subscribe as a supplier, Apple handles the payment. We receive a record of your subscription, not your card details.</li>
        <li>If you allow notifications, a code for your device so we can send them.</li>
      </ul>
      <h3>Safety information</h3>
      <ul>
        <li>Reports you make about content or other users.</li>
        <li>Users you have blocked.</li>
      </ul>
      <h3>On your device</h3>
      <p>The app saves unfinished drafts, such as a half-written repair report or bid, on your phone so you do not lose them. It also keeps you signed in. This stays on your device and is removed when you send the draft or sign out.</p>
      <h3>What we do not do</h3>
      <ul>
        <li>We do not show adverts.</li>
        <li>We do not use tracking or analytics tools.</li>
        <li>We do not sell your data.</li>
      </ul>
    </Section>

    <Section id="why-we-use-it" title="Why We Use It">
      <p>UK data protection law says we need a lawful reason (a “lawful basis”) for each use.</p>
      <Point lead="To provide the service you signed up for (contract).">
        <p>Creating your account, sending your repair reports, posting jobs, sending bids and passing the job address to the supplier you choose.</p>
      </Point>
      <Point lead="To keep Resident Service 360 safe and working (legitimate interests).">
        <p>Checking supplier details, dealing with reports, blocking and removing harmful content, preventing fraud, fixing problems and answering support requests.</p>
      </Point>
      <Point lead="To meet legal duties (legal obligation).">
        <p>For example, keeping records we are required to keep or responding to lawful requests from authorities.</p>
      </Point>
      <Point lead="Where you agree (consent).">
        <p>Photo help is optional. If you switch it on, the photos you add are sent to a third-party AI service so it can describe the problem for you. You can switch it off at any time in the app under Account, then Privacy. If we ever want to use your data for anything else optional, such as marketing emails, we will ask first.</p>
      </Point>
    </Section>

    <Section id="who-we-share-it-with" title="Who We Share It With">
      <Point lead="The supplier you choose.">
        <p>When you post a job, suppliers only see the general area. The exact service address is shared only with the supplier you pick.</p>
      </Point>
      <Point lead="Job posters.">
        <p>If you are a supplier, the person or business who posted a job sees your company name, your bid message and your price.</p>
      </Point>
      <Point lead="Your landlord or letting agent, only if you ask us.">
        <p>If you add who handles repairs and say we may contact them, we email them your repair report: your name, the address, the repair and your photos. Their replies go to your email address. If you say we may not contact them, we never do.</p>
      </Point>
      <Point lead="Local businesses we ask to quote.">
        <p>To find you quotes, we may send a short description of your job (the type of work, a summary and the general area) to local businesses, including ones that don't use Resident Service 360 yet. We never share your name, contact details, exact address or photos with them.</p>
      </Point>
      <Point lead="Service providers who work for us.">
        <p>They only use your data to provide their service to us:</p>
        <ul>
          <li>Supabase stores our database and handles sign-in.</li>
          <li>Netlify hosts our website and runs parts of our service.</li>
          <li>Anthropic (Claude) reads the photos you add, only if you have switched on Photo help. It isn't used to identify anyone.</li>
          <li>Google helps you find your address, finds nearby businesses for your job and runs Sign in with Google.</li>
          <li>Stripe handles card payments to suppliers.</li>
          <li>Apple handles supplier subscriptions, Sign in with Apple and notifications.</li>
          <li>Expo delivers notifications to your phone.</li>
          <li>Our email delivery service sends emails for us, including repair reports you ask us to send and invitations to local businesses.</li>
        </ul>
      </Point>
      <Point lead="Authorities.">
        <p>If the law requires it, or to protect someone from harm.</p>
      </Point>
      <p>Our database is hosted by Supabase in Ireland, in the European Economic Area, which the UK recognises as giving adequate protection to personal data. Some of our other providers process data in the United States, including Anthropic, Google, Apple, Stripe, Expo, Netlify and our email delivery service. When data goes to the United States, we rely on the UK Extension to the EU-US Data Privacy Framework where the provider is certified under it, or otherwise on the standard contractual clauses in the provider's data processing terms together with the UK International Data Transfer Addendum.</p>
    </Section>

    <Section id="housing-enquiries" title="Housing Enquiries"><p>If you send us a housing enquiry, our <Link to="/enquire/privacy">housing enquiry privacy notice</Link> explains how we use your answers, including any optional support or equality-monitoring information. Enquiries are reviewed by staff and are deleted after six months, or sooner on a valid withdrawal and deletion request. No account is needed.</p></Section>

    <Section id="how-long-we-keep-it" title="How Long We Keep It">
      <ul>
        <li>We keep your account details for as long as your account is open.</li>
        <li>When you delete your account, we delete or anonymise your personal data within 30 days.</li>
        <li>Repair reports, including those linked to a housing provider and those you send to a landlord, are kept for up to 6 years after the repair is closed, because that is how long a claim about a repair can usually be brought. After that we delete them or remove anything that identifies you.</li>
        <li>Photos sent for Photo help are not kept by us. The AI service may keep them for a limited time for safety and abuse checks under its own terms, and does not use them to identify you.</li>
        <li>Bids, job records and safety reports may be kept for up to 6 years to deal with disputes, meet UK record-keeping rules and keep the service safe.</li>
        <li>Our database provider keeps backups for up to 7 days. After that they are overwritten.</li>
      </ul>
    </Section>

    <Section id="your-rights" title="Your Rights">
      <p>You have the right to:</p>
      <ul>
        <li>ask for a copy of your data;</li>
        <li>ask us to correct anything that is wrong;</li>
        <li>ask us to delete your data;</li>
        <li>object to, or ask us to limit, how we use it;</li>
        <li>ask for your data in a format you can take elsewhere;</li>
        <li>withdraw consent where we rely on it.</li>
      </ul>
      <Point lead="Deleting your account.">
        <p>You can delete your account at any time in the app. Go to Account, then Delete account.</p>
      </Point>
      <p>For anything else, email <Email/>. We will reply within one month.</p>
    </Section>

    <Section id="keeping-your-data-safe" title="Keeping Your Data Safe">
      <p>Your data is sent over encrypted connections. Access is limited so people only see what they need, for example a supplier only sees an exact address once they are chosen.</p>
    </Section>

    <Section id="children" title="Children">
      <p>Resident Service 360 is not for anyone under 18. We do not knowingly collect data from children. If you think a child has made an account, please tell us and we will delete it.</p>
    </Section>

    <Section id="changes-to-this-policy" title="Changes to This Policy">
      <p>We may update this policy. We will change the date at the top when we do. If the changes are important, we will tell you in the app or by email.</p>
    </Section>

    <Section id="contact-us" title="Contact Us">
      <p>Email <Email/>. You can also write to {OPERATOR_NAME} at {REGISTERED_OFFICE}.</p>
    </Section>

    <Section id="complaints" title="Complaints">
      <p>If you are unhappy with how we have used your data, please contact us first so we can try to put it right. You also have the right to complain to the Information Commissioner's Office (ICO), the UK data protection regulator, at <a href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noreferrer">ico.org.uk/make-a-complaint</a> or on 0303 123 1113.</p>
    </Section>

    <p className="mk-legal-links">See also our <Link to="/terms">terms of use</Link> and <Link to="/support">support page</Link>.</p>
  </LegalShell>;
}

export function TermsPage() {
  return <LegalShell title="Terms of Use" draft>
    <Section id="about-these-terms" title="About These Terms">
      <p>These terms apply when you use the Resident Service 360 app or website. Resident Service 360 is run by {OPERATOR_NAME}, a company registered in England and Wales (company number 17114120), with its registered office at {REGISTERED_OFFICE} (“we”, “us”).</p>
      <p>By creating an account or using Resident Service 360, you agree to these terms. If you do not agree, please do not use Resident Service 360.</p>
    </Section>

    <Section id="what-resident-service-360-does" title="What Resident Service 360 Does">
      <p>Resident Service 360 connects people who need work done with suppliers who can do it. Residents can report repairs to their housing provider. Homeowners and businesses can post jobs. Suppliers can find work and send bids.</p>
      <p>Resident Service 360 does not carry out any work itself. We are not a party to any agreement between a job poster and a supplier.</p>
    </Section>

    <Section id="your-account" title="Your Account">
      <ul>
        <li>You must be 18 or over.</li>
        <li>The information you give us must be true and kept up to date.</li>
        <li>Keep your password safe. You are responsible for what happens on your account.</li>
        <li>If you act for a company, you must have its permission to do so.</li>
      </ul>
    </Section>

    <Section id="content-and-conduct-zero-tolerance" title="Content and Conduct: Zero Tolerance">
      <p>We have zero tolerance for objectionable content or abusive users. You must not post, send or upload anything that is:</p>
      <ul>
        <li>objectionable, offensive or abusive;</li>
        <li>harassing, bullying or threatening;</li>
        <li>discriminatory, including on the basis of race, religion, sex, sexual orientation, disability, age or any other protected characteristic;</li>
        <li>sexual or sexually explicit;</li>
        <li>violent, or encourages violence or harm;</li>
        <li>fraudulent, misleading or a scam, including fake jobs, fake bids or false company details;</li>
        <li>illegal, or encourages illegal activity;</li>
        <li>someone else's private information shared without their permission.</li>
      </ul>
      <Point lead="Your content.">
        <p>You keep ownership of what you post, send or upload, such as jobs, bids, photos, messages and reviews. You give us permission to store it, show it to the other users it is meant for and use it to run the service. You must only share content you have the right to share.</p>
      </Point>
      <Point lead="Reporting and blocking.">
        <p>You can report content and block other users in the app.</p>
      </Point>
      <Point lead="What we do.">
        <p>We review reports within 24 hours and act on them. We may remove content, and we may suspend or close the account of anyone who breaks these rules. We may do this without warning where there is a risk of harm.</p>
      </Point>
    </Section>

    <Section id="suppliers" title="Suppliers">
      <ul>
        <li>You are responsible for having the right qualifications, licences, certifications and insurance for any work you offer or carry out.</li>
        <li>Your company details, including your registration number, must be accurate.</li>
        <li>Bids must be honest. Only bid for work you can actually do.</li>
        <li>You must follow all laws that apply to your work, including health and safety rules.</li>
        <li>Only use a job address you are given to carry out that job.</li>
      </ul>
    </Section>

    <Section id="job-posters" title="Job Posters">
      <ul>
        <li>You choose which supplier to use. It is your decision.</li>
        <li>We do not check every supplier's work, qualifications or insurance. Please make your own checks before you agree to anything.</li>
        <li>Any agreement about price, timing or quality of work is between you and the supplier.</li>
      </ul>
    </Section>

    <Section id="no-guarantee-of-work" title="No Guarantee of Work">
      <p>We cannot promise that a job will get bids, that a bid will be accepted, or that a supplier will win any work through Resident Service 360.</p>
    </Section>

    <Section id="our-liability" title="Our Liability">
      <p>We work hard to keep Resident Service 360 running and accurate, but it is provided “as is”. We cannot promise it will always be available or free of errors.</p>
      <p>We are not responsible for work carried out by suppliers, or for disputes between users.</p>
      <p>We are not liable for any loss that was not foreseeable, or for loss of business, profit or opportunity.</p>
      <p>Where we are liable, our total liability to you is limited to £100 or the amount you paid us in the 12 months before the claim, whichever is higher.</p>
      <p>Nothing in these terms limits our liability for death or personal injury caused by our negligence, for fraud, or for anything else that cannot be limited by law. If you are a consumer, your legal rights are not affected.</p>
    </Section>

    <Section id="ending-your-account" title="Ending Your Account">
      <p>You can delete your account at any time in the app. Go to Account, then Delete account.</p>
      <p>We may suspend or close your account if you break these terms, if we need to protect other users, or if the law requires it. Where it is safe and reasonable, we will tell you why.</p>
    </Section>

    <Section id="changes" title="Changes">
      <p>We may update these terms. We will change the date at the top when we do. If the changes are important, we will tell you in the app or by email. If you keep using Resident Service 360 after that, you accept the new terms.</p>
    </Section>

    <Section id="governing-law" title="Governing Law">
      <p>These terms are governed by the law of England and Wales. Any dispute will be dealt with by the courts of England and Wales. If you live in Scotland or Northern Ireland, you can also bring a claim in your local courts.</p>
    </Section>

    <Section id="contact-us" title="Contact Us">
      <p>Email <Email/>.</p>
    </Section>

    <p className="mk-legal-links">See also our <Link to="/privacy">privacy policy</Link> and <Link to="/support">support page</Link>.</p>
  </LegalShell>;
}

export function SupportPage() {
  return <LegalShell title="Support">
    <Section id="get-help" title="Get Help">
      <p>Email us at <Email/>.</p>
      <p>We aim to reply within 2 working days.</p>
    </Section>

    <Section id="what-to-include" title="What to Include">
      <ul>
        <li>The email address on your Resident Service 360 account.</li>
        <li>What you were trying to do.</li>
        <li>What went wrong, and any error message you saw.</li>
        <li>Whether you were using the iPhone app or the website.</li>
        <li>A screenshot, if you can.</li>
      </ul>
      <p>Please do not send your password. We will never ask for it.</p>
    </Section>

    <Section id="report-a-problem-with-content-or-a-user" title="Report a Problem with Content or a User">
      <p>In the app, use the report option on the job, bid, message or profile. You can also block a user so they cannot contact you.</p>
      <p>We review reports within 24 hours. If someone is in immediate danger, call 999.</p>
    </Section>

    <Section id="delete-your-account" title="Delete Your Account">
      <p>In the app, go to Account, then Delete account. This removes your account and personal data as described in our <Link to="/privacy">privacy policy</Link>.</p>
      <p>If you cannot get into the app, email <Email/> from the address on your account and ask us to delete it.</p>
    </Section>

    <Section id="policies" title="Policies">
      <div className="mk-actions">
        <Link className="mk-button is-soft is-h52" to="/privacy">Privacy policy</Link>
        <Link className="mk-button is-soft is-h52" to="/terms">Terms of use</Link>
      </div>
    </Section>
  </LegalShell>;
}
